/* eslint-disable react-native/no-inline-styles */
import React from 'react';
import { View, Text, StyleSheet, ScrollView } from 'react-native';
import i18n from 'i18n-js';

import { Element, scale } from '@components/frame';
import { isPhone, isWeb } from '@helpers';

import Svg from '@styles/icons/svg';
import { font } from '@styles/fonts';
import TouchableElement from '@components/common/touchableElement';
import { SmallLoading } from '@components/loading';
import {
  LABEL_PATTERN,
  modalBg,
  modalFieldBg,
  SCREEN_BORDER_RADIUS,
  SCREEN_PADDING_HORIZONTAL,
} from '../../../v2/pages/common/constants/base.constants';
import { connect } from 'react-redux';
import EditableLine from '@components/common/editableLine';
import { updateLabelSwitchedOn } from '@reducers/language';

const Content = (props) => {
  const {
    C,
    hide,
    onSelect,
    onSubmit,
    data,
    selected,
    header,
    onCancel,
    maxHeight,
    loading,
    submitLoading,
    footerBlockConfig,
    onHeaderClick,
    itemOffset = 53,
    checkTopK = 0.37,
    titleTopK = 0.14,
    bodyTopK = 0.29,
    footnoteTopK = 0.49,
    testID,
  } = props;

  const headerHeight = scale(56);
  const footerHeight = scale(footerBlockConfig?.height ?? 0);
  const containerHeight = maxHeight - headerHeight - footerHeight;

  const calculatedMinHeight = scale(itemOffset) * data.length + headerHeight + footerHeight + scale(18);
  const minHeight = Math.min(calculatedMinHeight, containerHeight);

  return (
    <View testID={testID}>
      <View
        style={{
          height: headerHeight,
          borderTopLeftRadius: scale(SCREEN_BORDER_RADIUS),
          borderTopRightRadius: scale(SCREEN_BORDER_RADIUS),
        }}
      >
        <Element
          {...{
            config: {
              position: {
                top: 18,
                left: 14,
              },
              zIndex: 1000,
            },
          }}
        >
          <TouchableElement
            onPress={() => {
              onCancel();
              hide();
            }}
          >
            {({ boxStyle, pressHoverColor }) => (
              <View style={[boxStyle]}>
                <Text
                  style={{
                    ...font.Body,
                    color: C(pressHoverColor('Link/Light/Special')),
                  }}
                >
                  {i18n.t('Cancel')}
                </Text>
              </View>
            )}
          </TouchableElement>
        </Element>
        <Element
          {...{
            config: {
              position: {
                top: 18,
                right: 14,
              },
              zIndex: 1000,
            },
          }}
        >
          <TouchableElement
            onPress={async () => {
              if (submitLoading) {
                return;
              }

              if (onSubmit) {
                await onSubmit();
              }
              hide();
            }}
          >
            {({ boxStyle, pressHoverColor }) => (
              <View style={[boxStyle]}>
                {submitLoading ? (
                  <SmallLoading />
                ) : (
                  <Text
                    style={{
                      ...font.Headline,
                      color: C(pressHoverColor('Link/Light/Special')),
                    }}
                  >
                    {i18n.t('Done')}
                  </Text>
                )}
              </View>
            )}
          </TouchableElement>
        </Element>
        <Element
          {...{
            config: {
              width: '100%',
              position: {
                top: 18,
              },
              justifyContent: 'center',
            },
          }}
        >
          {onHeaderClick ? (
            <TouchableElement onPress={onHeaderClick}>
              {({}) => (
                <Text
                  style={[
                    {
                      color: C('Label / Light / Primary'),
                      textAlign: 'center',
                    },
                    font.Headline,
                  ]}
                >
                  {i18n.t(header)}
                </Text>
              )}
            </TouchableElement>
          ) : (
            <Text
              style={[
                {
                  color: C('Label / Light / Primary'),
                  textAlign: 'center',
                },
                font.Headline,
              ]}
            >
              {i18n.t(header)}
            </Text>
          )}
        </Element>
      </View>
      <View style={!isPhone ? { maxHeight: containerHeight, minHeight: minHeight } : { height: containerHeight }}>
        {loading ? (
          <View style={{ width: '100%', height: scale(200), justifyContent: 'center', alignContent: 'center', alignItems: 'center' }}>
            <SmallLoading />
          </View>
        ) : (
          <View style={[{ flex: 1 }, { gap: scale(20), paddingBottom: scale(18) }]}>
            <ScrollView
              style={{ flex: 1 }}
              contentContainerStyle={[
                {
                  paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL),
                  paddingTop: scale(18),
                },
              ]}
            >
              <View style={{ flexDirection: 'column' }}>
                {data.map((item, index) => {
                  return (
                    <TouchableElement
                      key={`${item.value}-${index}`}
                      onPress={() => {
                        onSelect(item);
                      }}
                      hitSlop={{ top: 0, bottom: 0, left: 0, right: 0 }}
                    >
                      {({ boxStyle, pressHoverColor }) => (
                        <View
                          style={[
                            boxStyle,
                            {
                              backgroundColor: C(pressHoverColor(modalFieldBg)),
                            },
                            index === 0 && {
                              borderTopLeftRadius: scale(10),
                              borderTopRightRadius: scale(10),
                            },
                            index === data.length - 1 && {
                              borderBottomLeftRadius: scale(10),
                              borderBottomRightRadius: scale(10),
                            },
                          ]}
                        >
                          <Element
                            {...{
                              config: {
                                customPosition: 'relative',
                                position: {
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                },
                                height: itemOffset,
                                paddingLeft: scale(16),
                              },
                            }}
                          >
                            {item.value === selected && (
                              <Element
                                {...{
                                  config: {
                                    position: {
                                      top: itemOffset * checkTopK,
                                      right: 16,
                                    },
                                    height: 14,
                                  },
                                }}
                              >
                                <Svg
                                  icon="check"
                                  color="Selected/Light/Primary"
                                  width={scale(isWeb ? 15 : 13.45)}
                                  height={scale(isWeb ? 15 : 13.45)}
                                />
                              </Element>
                            )}
                            <View
                              style={{
                                borderTopColor: C(pressHoverColor('Hairline / Light / No trans')),
                                borderTopWidth: index === 0 ? 0 : StyleSheet.hairlineWidth,
                                height: '100%',
                                zIndex: index,
                              }}
                            >
                              <Text
                                style={{
                                  ...font.Body,
                                  color: C(pressHoverColor('Label / Light / Primary')),
                                  position: 'absolute',
                                  top: scale(item.title ? itemOffset * titleTopK : itemOffset * bodyTopK),
                                  paddingRight: scale(40),
                                }}
                                numberOfLines={1}
                              >
                                {item.name}
                              </Text>
                              <Text
                                style={{
                                  ...font.Footnote,
                                  color: C(pressHoverColor('Label / Light / Tertiary')),
                                  position: 'absolute',
                                  top: scale(itemOffset * footnoteTopK),
                                }}
                                numberOfLines={1}
                              >
                                {i18n.t(item.title)}
                              </Text>
                            </View>
                          </Element>
                        </View>
                      )}
                    </TouchableElement>
                  );
                })}
              </View>
            </ScrollView>
            {footerBlockConfig && (
              <View style={{ gap: scale(3) }}>
                <EditableLine
                  {...{
                    item: {
                      value: footerBlockConfig?.value,
                      key: 'switchedOn',
                      name: footerBlockConfig?.title,
                      noArrow: true,
                      isLable: true,
                      oneLine: true,
                      small: false,
                      width: 170,
                      type: 'boolean',
                      loading: false,
                      onChange: footerBlockConfig?.action,
                    },
                    index: 0,
                    array: [{}],
                    C,
                    topBorder: false,
                    bottomBorder: false,
                    roundTop: true,
                    roundBottom: true,
                    threeColumns: false,
                    bg: modalFieldBg,
                    customStyles: {
                      paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL),
                    },
                    // trackOffColor: C(isPhone ? modalBg : 'Label/Light/Tertiary'),
                    // thumbOffColor: C(modalFieldBg),
                  }}
                />
                <Text
                  style={[
                    font['Caption 1'],
                    {
                      color: C('Label/Light/Tertiary'),
                      paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL * 2),
                    },
                  ]}
                >
                  {i18n.t(footerBlockConfig?.description)}
                </Text>
              </View>
            )}
          </View>
        )}
      </View>
    </View>
  );
};

const BaseModal = (props) => {
  return <Content {...{ ...props }} />;
};

export default BaseModal;
